import {getDsContext, isDsContextReady} from "@/utils/dsContext";
import {useEffect, useRef, useState} from "react";
import {MedicalAPI, isInjuryActiveAt} from "@/api/medical";
import {useSelector} from "react-redux";

import {CategoryAPI} from "@/api/category";
import {ClubAPI} from "@/api/club";
import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {StaffAPI} from "@/api/staff";
import {StaffRoleAPI} from "@/api/staff_role";
import {TrainingAPI} from "@/api/training";
import {UserRoleAPI} from "@/api/user_role";

import {ERROR_OPERATION} from "@/utils/constants";
import {
	loadClubAgents,
	loadClubCertificates,
	loadClubSnapshot,
	loadTeamFilter,
	saveTeamFilter,
	scopeCertificates,
	scopeSnapshot,
} from "@/utils/clubSnapshot";

// formatted -> formattazione per select
// onlyPro -> solo categorie agonistiche
// ordered -> ordinamento per il campo sorting
export const useCategories = ({
	setLoading,
	handleNotification,
	formatted = false,
	onlyPro = false,
	ordered = "descend",
}) => {
	const [categories, setCategories] = useState([]);

	useEffect(() => {
		getCategories();
	}, []);

	const getCategories = async () => {
		setLoading && setLoading(true);
		try {
			let response = await CategoryAPI.list();

			if (ordered === "descend") {
				response = response.sort((a, b) => {
					if (a.sorting > b.sorting) {
						return 1;
					} else if (a.sorting < b.sorting) {
						return -1;
					} else {
						return 0;
					}
				});
			}

			if (onlyPro) {
				response = response.filter((c) => !c.basicActivities);
			}

			if (formatted) {
				response = response.map((c) => {
					return {
						value: c.id,
						label: c.name,
						...c,
					};
				});
			}

			setCategories(response?.filter((c) => c.name !== "Non categorizzato"));
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {categories};
};

export const useClubs = ({setLoading, handleNotification}) => {
	const [clubs, setClubs] = useState([]);

	useEffect(() => {
		getClubs();
	}, []);

	const getClubs = async () => {
		setLoading && setLoading(true);
		try {
			const response = await ClubAPI.list();
			setClubs(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {clubs};
};

export const useMatches = ({setLoading, handleNotification, filters = {}}) => {
	const [matches, setMatches] = useState([]);
	const seasonId = useSelector((state) => state?.season?.id);

	useEffect(() => {
		getMatches();
	}, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const getMatches = async () => {
		setLoading && setLoading(true);
		try {
			const response = await MatchAPI.list({seasonId, ...filters});
			setMatches(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {matches};
};

// filtered -> no interni "esterni"
// goalkeepers -> ruolo portiere
// onlyInternal -> no interni "prestito"/"esterni"
// prospect -> giocatori scouting
export const usePlayers = ({
	setLoading,
	handleNotification,
	chargeData = true,
	filtered = true,
	goalkeepers = true,
	onlyMovementPlayers = false,
	onlyInternal = false,
	prospect,
}) => {
	const seasonId = useSelector((state) => state?.season?.id);
	const [players, setPlayers] = useState([]);
	const [playersLoading, setPlayersLoading] = useState(true);

	useEffect(() => {
		if (chargeData) {
			getPlayers();
		}
	}, [chargeData]);

	const getPlayers = async () => {
		setLoading && setLoading(true);
		setPlayersLoading(true);
		try {
			let response = await PlayerAPI.list(
				prospect !== undefined ? {prospect, seasonId} : {seasonId},
			);

			if (filtered) {
				response = response.filter((p) => p.status !== "Esterno");
			}

			if (onlyInternal) {
				response = response.filter((p) => p.status === "Interno");
			}

			if (goalkeepers) {
				response = response.filter((p) => p.goalkeeper);
			}

			if (onlyMovementPlayers) {
				response = response.filter((p) => !p.goalkeeper);
			}
			setPlayers(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
			setPlayersLoading(false);
		}
	};

	return {players, loading: playersLoading};
};

export const useTrainings = ({
	setLoading,
	handleNotification,
	filters = {},
}) => {
	const [trainings, setTrainings] = useState([]);
	const seasonId = useSelector((state) => state?.season?.id);

	useEffect(() => {
		getTrainings();
	}, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const getTrainings = async () => {
		setLoading && setLoading(true);
		try {
			const response = await TrainingAPI.list({seasonId, ...filters});
			setTrainings(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {trainings};
};

export const useStaffs = ({
	setLoading,
	handleNotification,
	chargeData = true,
	prospect,
}) => {
	const [staffs, setStaffs] = useState([]);

	useEffect(() => {
		if (chargeData) {
			getStaffs();
		}
	}, [chargeData]);

	const getStaffs = async () => {
		setLoading && setLoading(true);
		try {
			const response = await StaffAPI.list(
				prospect !== undefined ? {prospect} : {},
			);
			setStaffs(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {staffs};
};

export const useStaffRoles = ({setLoading, handleNotification}) => {
	const [staffRoles, setStaffRoles] = useState([]);

	useEffect(() => {
		getRoles();
	}, []);

	const getRoles = async () => {
		setLoading && setLoading(true);
		try {
			const response = await StaffRoleAPI.list();
			setStaffRoles(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {staffRoles};
};

export const useUserRoles = ({setLoading, handleNotification}) => {
	const [userRoles, setUserRoles] = useState([]);

	useEffect(() => {
		getRoles();
	}, []);

	const getRoles = async () => {
		setLoading && setLoading(true);
		try {
			const response = await UserRoleAPI.list();
			setUserRoles(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {userRoles};
};

export const useInjuryConfirmation = ({date, handleNotification}) => {
	const seasonId = useSelector((state) => state?.season?.id);
	const [injuryWarning, setInjuryWarning] = useState(null);
	const resolver = useRef(null);
	useEffect(() => () => resolver.current?.(false), []);
	const resolveInjuryWarning = (confirmed) => {
		resolver.current?.(confirmed);
		resolver.current = null;
		setInjuryWarning(null);
	};
	const confirmHealthyAttendance = async (rows) => {
		const ids = [
			...new Set(
				(Array.isArray(rows) ? rows : [rows])
					.filter((r) => r.status === "Presente")
					.map((r) => r.playerId),
			),
		];
		if (!ids.length) return true;
		try {
			const records = (
				await Promise.all(
					ids.map((playerId) => MedicalAPI.recordsList({playerId, seasonId})),
				)
			).flat();
			const affected = records.filter(
				(r) =>
					ids.includes(r.playerId) &&
					(r.injuriesIllness || []).some((inj) => isInjuryActiveAt(inj, date)),
			);
			if (!affected.length) return true;
			return await new Promise((resolve) => {
				resolver.current = resolve;
				setInjuryWarning(
					[
						...new Set(
							affected.map(
								(r) =>
									[r.playerData?.firstName, r.playerData?.lastName]
										.filter(Boolean)
										.join(" ") || "Giocatore",
							),
						),
					].join(", "),
				);
			});
		} catch {
			handleNotification(
				"Impossibile verificare lo stato medico. Riprova prima di salvare la presenza.",
				"error",
			);
			return false;
		}
	};
	return {injuryWarning, resolveInjuryWarning, confirmHealthyAttendance};
};

// Fotografia cross-area del club per la vista DS (condivisa e in cache).
// `data` è già ristretta alla squadra scelta nel filtro (null = tutte).
export const useClubSnapshot = ({setLoading, handleNotification} = {}) => {
	const season = useSelector((state) => state?.season);
	const context = useSelector(getDsContext);
	const [data, setData] = useState(null);
	const [error, setError] = useState(false);
	const [loading, setInternalLoading] = useState(true);
	const [teamId, setTeamIdState] = useState(loadTeamFilter);
	const requestId = useRef(0);
	const setTeamId = (id) => {
		setTeamIdState(id || null);
		saveTeamFilter(id || null);
	};
	useEffect(() => {
		setData(null);
		setTeamIdState(loadTeamFilter());
		if (season?.id && isDsContextReady()) fetchData();
		else setInternalLoading(false);
		return () => {
			requestId.current += 1;
		};
	}, [season?.id, context]); // eslint-disable-line react-hooks/exhaustive-deps
	const fetchData = async (force = false) => {
		setError(false);
		const current = ++requestId.current;
		const active = () =>
			current === requestId.current && context === getDsContext();
		setData(null);
		setLoading && setLoading(true);
		setInternalLoading(true);
		try {
			const response = await loadClubSnapshot(season, {force});
			if (active()) setData(response || null);
		} catch {
			if (active()) setError(true);
			if (active())
				handleNotification &&
					handleNotification(
						"Dati DS non disponibili o accesso non consentito. Riprova ad aggiornare.",
						"error",
					);
		} finally {
			if (active()) {
				setLoading && setLoading(false);
				setInternalLoading(false);
			}
		}
	};
	const visibleData = data?.context === context ? data : null;
	const teams = (visibleData?.teams || []).map((t) => ({
		id: t.id,
		name: t.name,
	}));
	const activeTeamId = teams.some((t) => t.id === teamId) ? teamId : null;
	return {
		data: scopeSnapshot(visibleData, activeTeamId),
		error,
		loading,
		reload: () => fetchData(true),
		scope: {teamId: activeTeamId, teams, setTeamId},
	};
};

// Stato certificati medico-sportivi dei giocatori della fotografia DS
export const useClubCertificates = ({snapshot, enabled = true}) => {
	const [data, setData] = useState(null);
	const [loading, setInternalLoading] = useState(false);

	useEffect(() => {
		setData(null);
		if (!snapshot || !enabled) {
			setInternalLoading(false);
			return;
		}
		let active = true;
		setInternalLoading(true);
		loadClubCertificates(snapshot)
			.then((response) => active && setData(response || null))
			.catch(() => active && setData(null))
			.finally(() => active && setInternalLoading(false));
		return () => {
			active = false;
		};
	}, [snapshot?.full || snapshot, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

	return {data: scopeCertificates(data, snapshot), loading};
};

// Procuratori e dettaglio contratti dei giocatori della fotografia DS
export const useClubAgents = ({snapshot}) => {
	const [data, setData] = useState(null);
	const [loading, setInternalLoading] = useState(true);

	useEffect(() => {
		if (!snapshot) return;
		let active = true;
		setInternalLoading(true);
		loadClubAgents(snapshot)
			.then((response) => active && setData(response || null))
			.catch(() => active && setData(null))
			.finally(() => active && setInternalLoading(false));
		return () => {
			active = false;
		};
	}, [snapshot?.full || snapshot]); // eslint-disable-line react-hooks/exhaustive-deps

	return {data, loading};
};
